/**
 * Forest drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const forestDrawingsPack: ToilePack = {
    slug: 'forest-drawings',
    category: 'illustration',
    name: 'Forest drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'path', label: 'Misty path', subject: 'a forest path between tall beech trunks fading into mist, roots crossing the track'},
        {key: 'pines', label: 'Pines at dusk', subject: 'a dense stand of pines on a slope at dusk, one shaft of light falling through a gap'},
        {key: 'clearing', label: 'Clearing', subject: 'an old oak in a clearing with a deer standing still at the edge of the trees'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['autumn', 'a fox', 'a river'],
    directionHint: 'try: autumn',
    seo: {
        title: 'Forest Drawings: Misty Path, Pines at Dusk, Oak Clearing',
        metaDescription: 'Nine forest drawings: a beech path in mist, pines at dusk and an old oak with a deer, each drawn in pencil, pen and ink, or charcoal.',
        h1: 'Forest drawings: a misty path, pines at dusk, an oak clearing',
        tagline: 'Three forest scenes, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three forest scenes: a path between beech trunks fading into mist, a stand of pines at dusk with one shaft of light, and an old oak in a clearing with a deer. Each is drawn in pencil, pen and ink, and charcoal, so the depth and the way in compare across the media. Type a different forest subject or a twist, such as autumn or a fox, and the same media carry it.',
        useCases: [
            'a sketchbook study of depth and mist',
            'a framed print for a quiet wall',
            'a reference for trunks and light',
            'a practice sheet for layered shading',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three forest scenes: a beech path in mist, pines at dusk, and an old oak with a deer. Each scene appears in three media.'},
            {q: 'Can I ask for a different forest?', a: 'Yes. Type a subject or twist, such as autumn, a fox or a river. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil shades from sharp detail to deep darks on plain white paper. Pen and ink uses contour lines, crosshatching and stippling. Charcoal uses smudged tone with lifted highlights.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The forest drawings are taller than they are wide, which suits trunks rising up the sheet.'},
        ],
    },
}
